/**
 * AdminIonEvalSubtab — AI eval dashboard (feature/ion-eval-admin-dashboard).
 *
 * Three views:
 *   - Runs: list of runs (pass@k / pass^k / cost / model / date) + a
 *     "Configure run" drawer that TRIGGERS a real, cost-gated Bedrock run
 *     in-app (Phase 6/7 — models/mechanisms/graders/repeats/baseline), a
 *     detail drawer with per-case pass/fail + failure reasons + a baseline
 *     delta column when the run has one, and a side-by-side comparison
 *     table for multi-model submissions (sequential execution — at most one
 *     model shows "running" at a time, the rest sit "queued").
 *   - Candidates: positive-feedback rows flagged `eval_candidate`, for
 *     review. Turning one into a case is an edit to
 *     `backend/app/api/v1/chat/eval/cases/cases.json` in a merge request — there is no
 *     promote button, because there is no row to promote into.
 *   - Cases: a read-only view of that corpus (`AdminIonEvalCasesSubtab.jsx`).
 *
 * Mirrors AdminIonFeedbackSubtab (list + filter + detail) and reuses the
 * admin-aim-* styles for visual consistency.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  getIonEvalCandidates,
  getIonEvalRuns,
  getIonEvalRun,
  getIonEvalCaseResult,
  getIonEvalRunGroup,
  exportIonEvalRunBundle,
  exportIonEvalRunMarkdown,
  exportIonEvalRunGroupBundle,
  exportIonEvalRunGroupMarkdown,
  getIonEvalBudget,
  grantIonEvalBudgetTopup,
  listIonEvalCases,
  triggerIonEvalRun,
} from '../../services/adminService.js';
import { AdminIonEvalCasesSubtab } from './AdminIonEvalCasesSubtab.jsx';
import { EvalCaseTreePicker, suiteCaseNameSet } from './EvalCaseTreePicker.jsx';
import { InfoTooltip } from './analytics/shared/InfoTooltip.jsx';
import {
  FIELD_INFO,
  failureReasonLabel,
  failureReasonTooltip,
  metricLabel,
  metricTooltip,
} from './ionEvalHelp.js';

// Static — no shared frontend model catalogue exists to reuse (chat model
// choice is server-side); mirrors CLAUDE.local.md's EU inference-profile table.
const EVAL_MODEL_OPTIONS = [
  { id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Haiku 4.5 (cheap)' },
  { id: 'eu.anthropic.claude-sonnet-4-6', label: 'Sonnet 4.6 (prod default)' },
  { id: 'eu.anthropic.claude-opus-4-6-v1', label: 'Opus 4.6 (hard reasoning)' },
];
// Eval engines (backend `EvalRunCreateRequest.engines`): classic is the
// customer Ion (ChatService on Bedrock); the native engines are Ion Next with
// its `ion` or `baseline` profile.
const EVAL_ENGINE_OPTIONS = [
  { id: 'classic', label: 'Classic (customer Ion, Bedrock)' },
  { id: 'native:ion', label: 'Ion Next (native:ion)' },
  { id: 'native:baseline', label: 'Ion Next baseline (native:baseline)' },
];
const ENGINE_IDS = EVAL_ENGINE_OPTIONS.map((e) => e.id);
// The only models the native engines run: backend `native/config.py`
// NATIVE_MODELS. Classic keeps EVAL_MODEL_OPTIONS.
const NATIVE_EVAL_MODEL_OPTIONS = [
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
];
const isNativeEngine = (engine) => engine !== 'classic';

/** A run's engine; rows from before engines existed carry none and are classic. */
const runEngine = (run) => run?.engine || 'classic';

/** The models an engine can run, as the backend's `eval_engine_models`. */
function engineModelOptions(engine) {
  return isNativeEngine(engine) ? NATIVE_EVAL_MODEL_OPTIONS : EVAL_MODEL_OPTIONS;
}

/**
 * Keep only the picked models some selected engine can run, and give each
 * selected engine family at least one model — the backend refuses an engine
 * with none (422), and a model no engine can run.
 */
function reconcileModels(models, engines) {
  const families = [];
  if (engines.includes('classic')) families.push(EVAL_MODEL_OPTIONS);
  if (engines.some(isNativeEngine)) families.push(NATIVE_EVAL_MODEL_OPTIONS);
  let kept = models.filter((id) => families.some((f) => f.some((m) => m.id === id)));
  for (const family of families) {
    if (!family.some((m) => kept.includes(m.id))) kept = [...kept, family[0].id];
  }
  return kept;
}

/**
 * Preset of the "Ion Next eval" view in AI Management: the same eval UI, with
 * the configurator opening on Ion Next, Opus 5.5 and the showcase suite.
 * Baseline and Sonnet 5.5 stay one click away; a suite the case list does not
 * carry is dropped when the cases load.
 */
export const ION_NEXT_EVAL_PRESET = {
  title: 'Ion Next eval',
  engines: ['native:ion'],
  models: ['claude-opus-5-5'],
  suite: 'ion_next_showcase',
};

const GRADER_OPTIONS = [
  { id: 'tool_correctness', label: 'Tool correctness (DeepEval)' },
  { id: 'hallucination', label: 'Hallucination (DeepEval, needs reference answer)' },
  { id: 'geval_rubric', label: 'GEval rubric (DeepEval, needs rubric text)' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function fmtPct(v) {
  return v == null ? '—' : `${Math.round(v * 100)}%`;
}

function fmtCost(v) {
  return v == null ? '—' : `$${Number(v).toFixed(2)}`;
}

/**
 * Remaining-budget line for the eval confirm banner.
 *
 * IMPORTANT — whose budget this is: eval spend is attributed to the SYNTHETIC
 * ION EVAL ACCOUNT (`chat/eval/eval_user.py`), never to the admin who clicks
 * "Run eval". `headroom` on both the 422 confirm body and the 202 accept body
 * is therefore a snapshot of THAT account's windows (and, on the 202, a
 * PRE-run snapshot — the launched run's own spend is not in it yet). An
 * earlier pass of this drawer called it "your remaining Ion budget", which was
 * simply false; don't reintroduce that wording.
 *
 * Values are plain USD decimal strings already floored at "0.00", so they are
 * interpolated as-is rather than re-parsed. Returns `null` when the whole
 * object is absent (backend older than this change) so the caller can omit the
 * line entirely instead of rendering an empty one.
 *
 * A `null` window means "no ceiling at all" — but that is NOT the same as the
 * account's configured value being the unlimited sentinel, and the two windows
 * differ in what `null` implies:
 *
 *  - **daily** normally carries a NUMBER even though this account's daily
 *    override IS the sentinel, because `ensure_within_quota` then applies the
 *    global runaway brake (`tier_limits_config.daily_usd_unlimited_cap`)
 *    instead, and the server reports that effective ceiling. It is the limit
 *    most likely to abort a real run part-way through the day, so `null` here
 *    (brake disabled, cap 0) is spelled out as such rather than as a bare
 *    "unlimited" — an earlier version of this function printed "today
 *    unlimited" on a $50 brake, hiding the only figure that mattered.
 *  - **weekly / monthly** have no brake behind them, so `null` there really is
 *    unbounded and reads "unlimited".
 *
 * The wire shape carries no `dailyIsBrake` flag (unlike `GET /ion-eval/budget`,
 * whose card can name the brake as the source), so the number is stated without
 * attribution here — the server's own 429/422 messages name the brake when it
 * is what refuses the run.
 */
function fmtHeadroomLine(headroom) {
  if (!headroom) return null;
  const parts = [
    ['today', headroom.dailyUsd, 'no cap (runaway brake disabled)'],
    ['this week', headroom.weeklyUsd, 'unlimited'],
    ['this month', headroom.monthlyUsd, 'unlimited'],
  ]
    .filter(([, usd]) => usd !== undefined)
    .map(([label, usd, noCeiling]) => `${label} ${usd == null ? noCeiling : `$${usd}`}`);
  if (!parts.length) return null;
  let line = `Ion eval account budget remaining: ${parts.join(' · ')}`;
  // `platformMonthlyUsd` is deliberately rendered as a SEPARATE clause with its
  // own label rather than as a fourth window above. It is not the eval
  // account's budget: it is the platform-wide monthly ceiling
  // (CHAT_MONTHLY_TOTAL_BUDGET_USD) over ALL Ion spend by every user, eval
  // included, which quota.ensure_within_quota checks first. Folding it into the
  // "eval account budget remaining" list would tell an admin to grant a top-up
  // for a ceiling a top-up cannot lift. `null` = the ceiling is switched off.
  if (headroom.platformMonthlyUsd !== undefined) {
    const platform =
      headroom.platformMonthlyUsd == null
        ? 'disabled'
        : `$${headroom.platformMonthlyUsd}`;
    line += ` — platform-wide Ion budget left this month (all users): ${platform}`;
  }
  return line;
}

// ---------------------------------------------------------------------------
// Partial runs — a run aborts mid-flight when the Ion eval account's own
// budget is exhausted between turns. The backend then sets status='partial'
// and MERGES these three keys onto the normal summary aggregates (which stay
// present and describe exactly `partial_cases_done` attempts, not the whole
// suite). Spellings mirror the module constants exported from
// `app/api/v1/chat/eval/runner.py` — keep them in sync.
// ---------------------------------------------------------------------------
const PARTIAL_REASON_KEY = 'partial_reason';
const PARTIAL_CASES_DONE_KEY = 'partial_cases_done';
const PARTIAL_CASES_TOTAL_KEY = 'partial_cases_total';
const PARTIAL_SUMMARY_KEYS = new Set([
  PARTIAL_REASON_KEY,
  PARTIAL_CASES_DONE_KEY,
  PARTIAL_CASES_TOTAL_KEY,
]);

/**
 * Render one `summary` value for the aggregates grid.
 *
 * The grid iterates EVERY key of `run.summary` generically, so a nested value
 * has to be handled here or it reaches `String(v)` and prints the literal
 * "[object Object]". `pass_rate_by_lang` ({de: 0.92, en: 0.88}) is the first
 * such value; flattening it inline is what makes per-language pass rates
 * actually readable, which is the point of reporting them at all.
 *
 * Deliberately generic rather than keyed on that one name: the next nested
 * aggregate someone adds should not have to remember to come back here.
 */
function formatSummaryValue(v) {
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const entries = Object.entries(v);
    if (entries.length === 0) return '—';
    return entries
      .map(([kk, vv]) => `${kk} ${typeof vv === 'number' ? Math.round(vv * 1000) / 1000 : String(vv)}`)
      .join(' · ');
  }
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v ?? '—');
}

const PARTIAL_STATUS_TOOLTIP =
  'Stopped early — the Ion eval account ran out of budget partway through. ' +
  'Only the cases that completed before the abort are stored, and the suite ' +
  'aggregates describe just those attempts, not the whole selection.';

/**
 * Human label for `summary.partial_reason`, whose value is
 * `"eval_user_quota_exhausted:<scope>"` with scope ∈ daily | weekly | monthly
 * | global_daily | global_monthly (the two platform-wide backstops). Unknown/
 * garbled values fall back to the raw string rather than being swallowed — a
 * run that says it stopped for a reason we can't name must still say so.
 *
 * The `global_*` scopes are worth naming distinctly: they mean the PLATFORM ran
 * out, not this eval account, so raising the eval budget will not unblock the
 * next run. `global_monthly` in particular resets only at the start of the next
 * UTC month.
 */
function partialReasonLabel(reason) {
  if (!reason) return 'Ion eval account budget exhausted mid-run.';
  const scope = String(reason).split(':')[1];
  const window = {
    daily: "the eval account's daily budget",
    weekly: "the eval account's weekly budget",
    monthly: "the eval account's monthly budget",
    global_daily: 'the platform-wide daily Ion backstop',
    global_monthly:
      'the platform-wide MONTHLY Ion spend ceiling (resets next UTC month)',
  }[scope];
  return window
    ? `Stopped mid-run: ${window} was exhausted.`
    : `Stopped mid-run: ${reason}`;
}

/**
 * Run status as a badge, shared by the runs list, the run detail drawer and
 * the model-comparison table so the three can't drift.
 *
 * `partial` gets its own colour + tooltip because it is neither of the two
 * outcomes the old done/failed pair could express: the results ARE real and
 * worth reading (unlike `failed`), but they cover only part of the selection
 * (unlike `done`), so scoring it green would silently overstate coverage.
 */
function RunStatusBadge({ status }) {
  if (status === 'partial') {
    return (
      <span className="admin-badge admin-badge--warning" title={PARTIAL_STATUS_TOOLTIP}>
        partial
      </span>
    );
  }
  const cls =
    status === 'done'
      ? 'admin-badge--basic'
      : status === 'queued' || status === 'running'
        ? ''
        : 'admin-badge--error';
  return <span className={`admin-badge ${cls}`}>{status || '—'}</span>;
}

/** Trigger a browser download of a text payload. */
function downloadBlob(text, filename, mime) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Trigger a browser download of a JSON bundle (run/group report export). */
function downloadJsonBundle(bundle, filename) {
  downloadBlob(JSON.stringify(bundle, null, 2), filename, 'application/json');
}

/** Trigger a browser download of a server-rendered Markdown report. */
function downloadMarkdownReport(text, filename) {
  downloadBlob(text, filename, 'text/markdown');
}

/**
 * Repeats>1 writes one IonEvalCaseResult row per repeat with the SAME
 * case_name and no repeat_index column (would need a migration — not done
 * here, see AdminIonEvalSubtab module docstring / handoff notes). Until
 * that column exists, badge occurrence order client-side (in list order,
 * which is DB insertion order) so admins aren't confused by literal
 * duplicate rows with no way to tell them apart.
 */
function withRepeatOrdinals(cases) {
  const seen = new Map();
  const totals = new Map();
  for (const c of cases) totals.set(c.caseName, (totals.get(c.caseName) || 0) + 1);
  return cases.map((c) => {
    const n = (seen.get(c.caseName) || 0) + 1;
    seen.set(c.caseName, n);
    const total = totals.get(c.caseName) || 1;
    return { ...c, _repeatOrdinal: n, _repeatTotal: total };
  });
}

function ToolCallRow({ call }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="admin-aim-field" style={{ border: '1px solid var(--border-color, #ccc)', borderRadius: 6, padding: 8, marginBottom: 6 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span>{expanded ? '▾' : '▸'}</span>
        <code>{call.name}</code>
        {call.is_error && <span className="admin-badge admin-badge--error">error</span>}
      </div>
      {expanded && (
        <div style={{ marginTop: 6, fontSize: 12 }}>
          <div><strong>Input:</strong></div>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>
            {JSON.stringify(call.input ?? {}, null, 2)}
          </pre>
          <div><strong>Output:</strong></div>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>
            {call.output === undefined
              ? '(not captured — run predates output capture)'
              : call.output === null
                ? '(none)'
                : JSON.stringify(call.output, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function CaseDetailDrawer({ runId, caseResultId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    getIonEvalCaseResult(runId, caseResultId)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'network'); });
    return () => { cancelled = true; };
  }, [runId, caseResultId]);

  const traj = detail?.trajectory;
  const deepeval = detail?.deepevalMetrics;
  const m = detail?.metrics || {};

  return (
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div className="admin-aim-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">Case: {detail?.caseName || '…'}</div>
            {detail && (
              <div className="admin-aim-drawer-sub">
                {detail.passed ? 'pass' : 'fail'}
                {detail.failureReason ? ` · ${failureReasonLabel(detail.failureReason)}` : ''}
              </div>
            )}
          </div>
          <button type="button" className="admin-aim-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="admin-aim-drawer-body">
          {error && <div className="admin-aim-error">{error}</div>}
          {!detail && !error && <p>Loading…</p>}
          {detail && (
            <>
              <div className="admin-aim-field">
                <label>Prompt {detail.caseDefinitionFound === false && (
                  <span className="admin-badge admin-badge--error" title="The deployed corpus carries no case by this name — it was renamed or removed in a merge request since this run executed. This may not be the exact prompt that ran.">
                    case since edited/deleted
                  </span>
                )}</label>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {detail.caseDefinition?.prompt || '(prompt unavailable — the deployed corpus carries no case by this name; no prompt snapshot is stored per result)'}
                </p>
              </div>

              {detail.failureReason && (
                <div className="admin-aim-field">
                  <label>Failure reason</label>
                  <p>
                    <strong>{failureReasonLabel(detail.failureReason)}</strong>
                    {failureReasonTooltip(detail.failureReason) && (
                      <span style={{ display: 'block', fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                        {failureReasonTooltip(detail.failureReason)}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {m.tool_correctness && (m.tool_correctness.missing?.length || m.tool_correctness.forbidden_fired?.length || m.tool_correctness.extra?.length) ? (
                <div className="admin-aim-field">
                  <label>Tool correctness — expected vs actual</label>
                  <div style={{ fontSize: 13 }}>
                    <div>Expected: {(m.tool_correctness.expected || []).join(', ') || '—'}</div>
                    <div>Actual: {(m.tool_correctness.actual || []).join(', ') || '—'}</div>
                    {m.tool_correctness.missing?.length > 0 && <div style={{ color: 'var(--danger, #c0392b)' }}>Missing: {m.tool_correctness.missing.join(', ')}</div>}
                    {m.tool_correctness.forbidden_fired?.length > 0 && <div style={{ color: 'var(--danger, #c0392b)' }}>Forbidden fired: {m.tool_correctness.forbidden_fired.join(', ')}</div>}
                  </div>
                </div>
              ) : null}

              {m.output_assertions && (m.output_assertions.missing_required?.length > 0 || m.output_assertions.present_forbidden?.length > 0) ? (
                <div className="admin-aim-field">
                  <label>Output assertions — expected vs actual</label>
                  <div style={{ fontSize: 13 }}>
                    {m.output_assertions.missing_required?.length > 0 && (
                      <div style={{ color: 'var(--danger, #c0392b)' }}>Missing required substrings: {m.output_assertions.missing_required.join(', ')}</div>
                    )}
                    {m.output_assertions.present_forbidden?.length > 0 && (
                      <div style={{ color: 'var(--danger, #c0392b)' }}>Forbidden substrings present: {m.output_assertions.present_forbidden.join(', ')}</div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Refusal gets an expected-vs-actual block like the assertion
                  blocks above, but it is the ONE heuristic assertion in the
                  harness (`refusal_check` = lexical decline-phrase match in the
                  opening ~400 chars, suppressed by a [[V:N]] citation or a long
                  answer). So a `refusal_mismatch` was previously unreviewable:
                  a bare label with none of the evidence needed to tell a real
                  regression from a miss by the detector. The evidence row is
                  therefore shown for a PASS too — a pass that only held because
                  the answer tripped the "substantive" suppressor is worth
                  seeing.

                  `expected` is derived from the metric itself (passed XOR
                  refused) rather than read from `caseDefinition.expectRefusal`,
                  because caseDefinition is a best-effort lookup against the
                  deployed corpus file and is null once the case is renamed or
                  removed in a merge request — see the
                  `caseDefinitionFound` badge on the prompt above. Derivation is
                  exact whenever `applicable` is true. */}
              {m.refusal && m.refusal.applicable && (
                <div className="admin-aim-field">
                  <label>Refusal — expected vs actual</label>
                  <div style={{ fontSize: 13 }}>
                    <div>
                      Expected: {(m.refusal.passed === m.refusal.refused) ? 'refusal' : 'substantive answer'}
                    </div>
                    <div style={m.refusal.passed ? undefined : { color: 'var(--danger, #c0392b)' }}>
                      Actual: {m.refusal.refused ? 'refused' : 'did not refuse'}
                    </div>
                    <div style={{ opacity: 0.8, marginTop: 2 }}>
                      Decline phrases matched:{' '}
                      {m.refusal.matched_markers?.length
                        ? m.refusal.matched_markers.map((s) => `“${s}”`).join(', ')
                        : 'none'}
                      {' · '}treated as substantive: {String(m.refusal.substantive_answer)}
                      {' ('}citation={String(m.refusal.has_citation)}, {m.refusal.n_words} words
                      {') · '}window: first {m.refusal.window_chars} chars
                      {m.refusal.detector ? ` · ${m.refusal.detector}` : ''}
                    </div>
                  </div>
                </div>
              )}

              {m.citation && m.citation.applicable && (
                <div className="admin-aim-field">
                  <label>Citation — expected vs actual</label>
                  <div style={{ fontSize: 13 }}>
                    has_citation={String(m.citation.has_citation)}, passed={String(m.citation.passed)}
                  </div>
                </div>
              )}

              {/* expect_value_range (`value_in_range` in deterministic.py): the
                  range, the number that matched, and every candidate the scorer
                  found (converted to the range's unit), so a
                  `value_out_of_range` can be told apart from a unit the parser
                  missed. */}
              {m.value_in_range && m.value_in_range.applicable && m.value_in_range.expected && (
                <div className="admin-aim-field">
                  <label>Value range — expected vs actual</label>
                  <div style={{ fontSize: 13 }}>
                    <div>
                      Expected: {m.value_in_range.expected.min}–{m.value_in_range.expected.max} {m.value_in_range.expected.unit}
                    </div>
                    <div style={m.value_in_range.passed ? undefined : { color: 'var(--danger, #c0392b)' }}>
                      Matched: {m.value_in_range.matched
                        ? `${m.value_in_range.matched.converted} ${m.value_in_range.expected.unit} (${m.value_in_range.matched.source})`
                        : 'none in range'}
                    </div>
                    <div style={{ opacity: 0.8, marginTop: 2 }}>
                      Numbers found:{' '}
                      {m.value_in_range.candidates?.length
                        ? m.value_in_range.candidates
                          .map((c) => `${c.converted} ${m.value_in_range.expected.unit} (${c.source}${c.in_range ? ', in range' : ''})`)
                          .join(', ')
                        : 'none with a unit of this dimension'}
                    </div>
                  </div>
                </div>
              )}

              <div className="admin-aim-field">
                <label>Stop reason</label>
                <p>{traj?.stop_reason || '—'}</p>
              </div>

              <div className="admin-aim-field">
                <label>Tool-call trajectory ({traj?.tool_calls?.length ?? 0})</label>
                {!traj || !traj.tool_calls?.length ? (
                  <p className="admin-aim-field-hint">No tool calls in this turn.</p>
                ) : (
                  traj.tool_calls.map((tc, i) => <ToolCallRow key={i} call={tc} />)
                )}
              </div>

              <div className="admin-aim-field">
                <label>Final model answer</label>
                <p style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                  {traj?.final_text || '(empty)'}
                </p>
              </div>

              {deepeval && Object.keys(deepeval).length > 0 && (
                <div className="admin-aim-field">
                  <label>DeepEval grader scores</label>
                  {Object.entries(deepeval).map(([name, d]) => (
                    <div key={name} style={{ marginBottom: 6, fontSize: 13 }}>
                      <strong>{name}:</strong>{' '}
                      <span className={`admin-badge ${d?.success ? 'admin-badge--basic' : 'admin-badge--error'}`}>
                        {typeof d?.score === 'number' ? d.score.toFixed(2) : '—'}
                      </span>
                      {d?.reason && <div style={{ opacity: 0.85 }}>{d.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RunDetail({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [openCaseId, setOpenCaseId] = useState(null);
  const [dlError, setDlError] = useState(null);

  const handleDownloadReport = async () => {
    setDlError(null);
    try {
      const bundle = await exportIonEvalRunBundle(runId);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadJsonBundle(bundle, `ion-eval-run-${runId}-${stamp}.json`);
    } catch (e) {
      setDlError(e?.message || 'download failed');
    }
  };

  const handleDownloadMarkdown = async () => {
    setDlError(null);
    try {
      const md = await exportIonEvalRunMarkdown(runId);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadMarkdownReport(md, `ion-eval-run-${runId}-${stamp}.md`);
    } catch (e) {
      setDlError(e?.message || 'download failed');
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = () => {
      getIonEvalRun(runId)
        .then((data) => {
          if (cancelled) return;
          setRun(data);
          // Poll while the run is still in flight so progress_json / final
          // status show up without the admin needing to re-open the drawer.
          if (data.status === 'queued' || data.status === 'running') {
            timer = setTimeout(load, 4000);
          }
        })
        .catch((e) => {
          if (!cancelled) setError(e?.message || 'network');
        });
    };
    load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId]);

  return (
    <>
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div className="admin-aim-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">Eval run #{runId}</div>
            {run && (
              <div className="admin-aim-drawer-sub">
                {runEngine(run)} · {run.modelId || '—'} · {run.caseSet || 'default'} · {fmtDate(run.createdAt)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleDownloadReport}
              disabled={!run}
              title="Download this run as a self-contained JSON report (config, summary, and every per-case trajectory + judge score)."
            >
              ⬇ Download report (JSON)
            </button>
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleDownloadMarkdown}
              disabled={!run}
              title="Download this run as a self-contained Markdown report — config, aggregates, failure index, and per case the prompt, expectations, actual answer, tool calls, failed assertions and judge reasons. Built to paste into an AI assistant."
            >
              ⬇ Download report (Markdown)
            </button>
            <button type="button" className="admin-aim-icon-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
        </header>
        <div className="admin-aim-drawer-body">
          {error && <div className="admin-aim-error">{error}</div>}
          {dlError && <div className="admin-aim-error">Report download failed: {dlError}</div>}
          {!run && !error && <p>Loading…</p>}
          {run && (
            <>
              <div className="admin-aim-field-grid" style={{ marginBottom: 12 }}>
                <div><strong>Status:</strong> <RunStatusBadge status={run.status} /></div>
                <div><strong>Engine:</strong> {runEngine(run)} <InfoTooltip title="Engine" description={FIELD_INFO.engines} /></div>
                {(run.status === 'queued' || run.status === 'running') && run.progress && (
                  <div><strong>Progress:</strong> {run.progress.cases_done ?? 0} / {run.progress.cases_total ?? '?'}</div>
                )}
                <div><strong>Repeats:</strong> {run.repeats ?? '—'} <InfoTooltip title="Repeats" description={FIELD_INFO.repeats} /></div>
                <div><strong>Judge:</strong> {run.judgeEnabled ? 'on' : 'off'} <InfoTooltip title="Judge" description={FIELD_INFO.judge} /></div>
                <div><strong>Git SHA:</strong> {run.gitSha || '—'} <InfoTooltip title="Git SHA" description={FIELD_INFO.gitSha} /></div>
                <div><strong>Prompt version:</strong> {run.promptVersion || '—'} <InfoTooltip title="Prompt version" description={FIELD_INFO.promptVersion} /></div>
                <div><strong>Estimated cost:</strong> {fmtCost(run.estimatedCostUsd)}</div>
                <div><strong>Actual cost:</strong> {fmtCost(run.actualCostUsd)}</div>
                <div><strong>Triggered by:</strong> {run.createdByEmail || '—'}</div>
                {run.baselineRunId != null && <div><strong>Baseline:</strong> run #{run.baselineRunId} <InfoTooltip title="Baseline run" description={FIELD_INFO.baselineRun} /></div>}
              </div>

              {/*
                A partial run's aggregates are computed over the attempts that
                actually ran, so they look like a normal (often better or worse
                by luck) suite result. Say so loudly above them — the numbers
                themselves carry no hint that the selection was cut short.
              */}
              {run.status === 'partial' && (
                <div
                  className="admin-aim-error-banner"
                  style={{ background: 'var(--warning, #c98a0d)', marginBottom: 12 }}
                >
                  <div style={{ fontWeight: 600 }}>Partial run — the eval budget ran out mid-run</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {partialReasonLabel(run.summary?.[PARTIAL_REASON_KEY])}{' '}
                    {run.summary?.[PARTIAL_CASES_TOTAL_KEY] != null && (
                      <>
                        {run.summary[PARTIAL_CASES_DONE_KEY] ?? 0} of{' '}
                        {run.summary[PARTIAL_CASES_TOTAL_KEY]} attempts completed.{' '}
                      </>
                    )}
                    The aggregates and case rows below cover only those attempts — read them
                    as a sample, not as a suite result. Raise the Ion eval account's budget
                    in AI Management → Usages and re-run for full coverage.
                  </div>
                </div>
              )}

              {run.summary && Object.keys(run.summary).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>
                    Suite aggregates
                    {run.status === 'partial' ? ' (completed attempts only)' : ''}
                    {run.baselineSummary ? ' (Δ vs baseline)' : ''}
                  </h3>
                  <div className="admin-aim-field-grid">
                    {/* partial_* keys are rendered by the banner above, not as pseudo-metrics. */}
                    {Object.entries(run.summary).filter(([k]) => !PARTIAL_SUMMARY_KEYS.has(k)).map(([k, v]) => {
                      const baselineV = run.baselineSummary ? run.baselineSummary[k] : undefined;
                      const isNum = typeof v === 'number' && typeof baselineV === 'number';
                      const delta = isNum ? v - baselineV : null;
                      return (
                        <div key={k}>
                          <strong>{metricLabel(k)}:</strong> {formatSummaryValue(v)}
                          {metricTooltip(k) && <InfoTooltip title={metricLabel(k)} description={metricTooltip(k)} />}
                          {isNum && (
                            <span style={{ marginLeft: 6, color: delta >= 0 ? 'var(--success, #0d7c99)' : 'var(--danger, #c0392b)' }}>
                              ({delta >= 0 ? '+' : ''}{Math.round(delta * 1000) / 1000})
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>Cases ({run.cases?.length ?? 0})</h3>
              <p className="admin-aim-field-hint" style={{ marginTop: 0 }}>Click a row to inspect the prompt, tool-call trajectory, final answer, and the exact expected-vs-actual assertion behind its failure reason.</p>
              <div className="admin-aim-table-wrap">
                <table className="admin-aim-table">
                  <thead>
                    <tr>
                      <th>Case</th>
                      <th>Provenance</th>
                      <th>Passed</th>
                      <th>Failure reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(run.cases || []).length === 0 ? (
                      <tr><td colSpan={4} className="admin-aim-empty">No case results.</td></tr>
                    ) : withRepeatOrdinals(run.cases).map((c) => (
                      <tr key={c.id} onClick={() => setOpenCaseId(c.id)} style={{ cursor: 'pointer' }}>
                        <td>
                          {c.caseName}
                          {c._repeatTotal > 1 && (
                            <span
                              className="admin-badge"
                              style={{ marginLeft: 6 }}
                              title="repeats>1 does not yet store a repeat_index column (needs a migration) — this is the row's occurrence order among same-named results, not a guaranteed repeat number."
                            >
                              repeat {c._repeatOrdinal}/{c._repeatTotal}
                            </span>
                          )}
                        </td>
                        <td>{c.provenance || '—'}</td>
                        <td>
                          <span className={`admin-badge ${c.passed ? 'admin-badge--basic' : 'admin-badge--error'}`}>
                            {c.passed ? 'pass' : 'fail'}
                          </span>
                        </td>
                        <td title={failureReasonTooltip(c.failureReason) || undefined}>
                          {failureReasonLabel(c.failureReason)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    {openCaseId != null && (
      <CaseDetailDrawer runId={runId} caseResultId={openCaseId} onClose={() => setOpenCaseId(null)} />
    )}
    </>
  );
}

function RunConfiguratorDrawer({ onClose, onSubmitted, preset = null }) {
  const [allCases, setAllCases] = useState([]);
  const [selectedCaseNames, setSelectedCaseNames] = useState([]);
  const [suite, setSuite] = useState(preset?.suite || '');
  const [engines, setEngines] = useState(preset?.engines || ['classic']);
  const [models, setModels] = useState(() =>
    reconcileModels(preset?.models || [EVAL_MODEL_OPTIONS[0].id], preset?.engines || ['classic']),
  );
  const [repeats, setRepeats] = useState(1);
  const [toolsEnabled, setToolsEnabled] = useState(true);
  const [webSearchEnabled, setWebSearchEnabled] = useState(true);
  const [knowledgeEnabled, setKnowledgeEnabled] = useState(true);
  const [temperature, setTemperature] = useState('');
  const [graders, setGraders] = useState([]);
  const [judgeModel, setJudgeModel] = useState('');
  const [priorRuns, setPriorRuns] = useState([]);
  const [baselineRunId, setBaselineRunId] = useState('');
  // {estimatedCostUsd, headroom, exceedsHeadroom, ...} while awaiting confirm
  const [estimate, setEstimate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // {kind, message} for the two NON-overridable 429 pre-flight refusals (the
  // per-run cost ceiling and the eval account's exhausted budget). Kept apart
  // from `error` (transient/unexpected failures) and from `estimate` (the 422
  // confirm step) because neither of these can be resolved by confirming — the
  // config itself has to change, so the banner must say so instead of leaving
  // the admin clicking "Confirm".
  const [blocked, setBlocked] = useState(null);

  useEffect(() => {
    listIonEvalCases({})
      .then((data) => {
        const cases = data.cases || [];
        setAllCases(cases);
        // A preset suite the corpus does not (yet) carry would 400 the run;
        // drop it so the picker shows its normal empty selection instead.
        if (preset?.suite && !cases.some((c) => c.suite === preset.suite)) setSuite('');
      })
      .catch(() => {});
    getIonEvalRuns({ limit: 50 }).then((data) => setPriorRuns(data.items || [])).catch(() => {});
  }, []);

  const hasNative = engines.some(isNativeEngine);
  const isClassicOnly = engines.length === 1 && engines[0] === 'classic';
  // One run per (engine, model the engine can run) — the backend's candidate_runs().
  const runCount = engines.reduce(
    (n, engine) => n + models.filter((id) => engineModelOptions(engine).some((m) => m.id === id)).length,
    0,
  );
  const toggleEngine = (id) => {
    const picked = engines.includes(id) ? engines.filter((e) => e !== id) : [...engines, id];
    const next = ENGINE_IDS.filter((e) => picked.includes(e));
    setEngines(next);
    setModels((prev) => reconcileModels(prev, next));
    setEstimate(null);
  };

  const suites = useMemo(
    () => [...new Set(allCases.map((c) => c.suite).filter(Boolean))].sort(),
    [allCases],
  );

  // Cases belonging to the selected suite — pre-checked in the picker below so
  // the suite union is visible BEFORE submit, not only in the post-submit
  // estimate banner. ACTIVE cases only: `resolve_eval_case_names()` filters
  // `is_active`, so counting inactive members here over-stated both the case
  // count and the cost estimate.
  const suiteCaseNames = useMemo(() => suiteCaseNameSet(allCases, suite), [allCases, suite]);
  const activeCaseNames = useMemo(
    () => new Set(allCases.filter((c) => c.isActive).map((c) => c.caseName)),
    [allCases],
  );
  // Live running total: explicit picks UNION suite membership (the actual
  // resolve_eval_case_names() semantics on the backend) — not just the
  // checkbox count, which would under-count suite-only cases. Inactive picks
  // are excluded for the same reason as above: the backend drops them, so
  // counting one here promises a case the run will never execute.
  const unionCaseNames = useMemo(() => {
    const union = new Set(selectedCaseNames.filter((name) => activeCaseNames.has(name)));
    for (const name of suiteCaseNames) union.add(name);
    return union;
  }, [selectedCaseNames, suiteCaseNames, activeCaseNames]);
  const unionCaseCount = unionCaseNames.size;
  // A grader whose per-case input field is empty silently grades nothing. The
  // corpus currently has ZERO reference answers, so selecting the hallucination
  // grader is a no-op that looks like a configured check — say so inline.
  const graderCoverage = useMemo(() => {
    const scope = unionCaseNames.size
      ? allCases.filter((c) => unionCaseNames.has(c.caseName))
      : allCases.filter((c) => c.isActive);
    return {
      total: scope.length,
      withReferenceAnswer: scope.filter((c) => c.referenceAnswer).length,
      withRubric: scope.filter((c) => c.rubricText).length,
    };
  }, [allCases, unionCaseNames]);

  const toggleModel = (id) => {
    setModels((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };
  const toggleGrader = (id) => {
    setGraders((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };
  const buildPayload = (confirm) => ({
    caseNames: selectedCaseNames.length ? selectedCaseNames : null,
    suite: suite || null,
    models,
    repeats,
    // Native engines always offer their tools (the backend refuses false).
    toolsEnabled: hasNative ? true : toolsEnabled,
    webSearchEnabled,
    knowledgeEnabled,
    temperature: temperature === '' ? null : Number(temperature),
    graders,
    judgeModel: judgeModel || null,
    baselineRunId: baselineRunId ? Number(baselineRunId) : null,
    confirm,
    // Classic only = the server default: no `engines` key, the payload stays
    // exactly what it was before engines existed.
    ...(isClassicOnly ? {} : { engines }),
  });

  const handleSubmit = async (confirm) => {
    if (!engines.length) {
      setError('Select at least one engine.');
      return;
    }
    if (!models.length) {
      setError('Select at least one model.');
      return;
    }
    const engineWithoutModel = engines.find(
      (engine) => !models.some((id) => engineModelOptions(engine).some((m) => m.id === id)),
    );
    if (engineWithoutModel) {
      setError(`Select at least one model that ${engineWithoutModel} can run.`);
      return;
    }
    if (!selectedCaseNames.length && !suite) {
      setError('Select at least one case or a suite.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setBlocked(null);
    try {
      const result = await triggerIonEvalRun(buildPayload(confirm));
      if (result._needsConfirm) {
        setEstimate(result);
        return;
      }
      onSubmitted(result);
    } catch (e) {
      if (e?.status === 429) {
        // Refused before anything ran. Drop any pending estimate: the confirm
        // path is moot now (the refusal is evaluated ahead of the confirm gate
        // and ignores confirm=true), so the footer must go back to "Run eval"
        // for a re-scoped selection.
        setBlocked({ kind: e.kind, message: e.message });
        setEstimate(null);
      } else {
        setError(e?.message || 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Only ever shown alongside the estimate banner — the 202 path closes the
  // drawer, and its headroom snapshot (the eval account's, pre-run) was
  // already displayed here before the admin confirmed.
  const headroomLine = estimate ? fmtHeadroomLine(estimate.headroom) : null;

  return (
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div className="admin-aim-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">Configure eval run</div>
            <div className="admin-aim-drawer-sub">
              Triggers real {hasNative ? 'model' : 'Bedrock'} spend — billed to the Ion eval account, not to you
            </div>
          </div>
          <button type="button" className="admin-aim-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="admin-aim-drawer-body">
          <div className="admin-aim-field">
            <label>Suite (optional — unions with explicit case picks below) <InfoTooltip title="Suite" description={FIELD_INFO.suite} /></label>
            <select value={suite} onChange={(e) => setSuite(e.target.value)}>
              <option value="">— none —</option>
              {suites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="admin-aim-field">
            <label>
              Cases ({unionCaseCount} total{suite ? ` — ${selectedCaseNames.length} explicit + suite "${suite}"` : ''})
            </label>
            <EvalCaseTreePicker
              cases={allCases}
              selectedNames={selectedCaseNames}
              onChange={setSelectedCaseNames}
              suiteFromDropdown={suite}
            />
            <p className="admin-aim-field-hint">
              Hover a case to see its prompt, expectations and which graders it needs.
            </p>
          </div>
          <div className="admin-aim-field">
            <label>Engines <InfoTooltip title="Engines" description={FIELD_INFO.engines} /></label>
            {EVAL_ENGINE_OPTIONS.map((e) => (
              <label key={e.id} style={{ display: 'block', fontSize: 13 }}>
                <input
                  type="checkbox"
                  name="eval-engine"
                  value={e.id}
                  checked={engines.includes(e.id)}
                  onChange={() => toggleEngine(e.id)}
                />
                {' '}{e.label}
              </label>
            ))}
          </div>
          <div className="admin-aim-field">
            <label>Models</label>
            {engines.includes('classic') && EVAL_MODEL_OPTIONS.map((m) => (
              <label key={m.id} style={{ display: 'block', fontSize: 13 }}>
                <input type="checkbox" name="eval-model" value={m.id} checked={models.includes(m.id)} onChange={() => toggleModel(m.id)} />
                {' '}{m.label}{hasNative ? ' (classic)' : ''}
              </label>
            ))}
            {hasNative && NATIVE_EVAL_MODEL_OPTIONS.map((m) => (
              <label key={m.id} style={{ display: 'block', fontSize: 13 }}>
                <input type="checkbox" name="eval-model" value={m.id} checked={models.includes(m.id)} onChange={() => toggleModel(m.id)} />
                {' '}{m.label}{engines.includes('classic') ? ' (Ion Next)' : ''}
              </label>
            ))}
            {runCount > 1 && (
              <p className="admin-aim-field-hint">
                {runCount} runs (engine × model), side by side in one comparison.
              </p>
            )}
          </div>
          <div className="admin-aim-field-grid">
            <div className="admin-aim-field">
              <label>Repeats <InfoTooltip title="Repeats" description={FIELD_INFO.repeats} /></label>
              <input type="number" min={1} max={20} value={repeats} onChange={(e) => setRepeats(Number(e.target.value) || 1)} placeholder="3-5 typical" />
              <p className="admin-aim-field-hint">
                repeats × models × cases = total LLM calls. 1 repeat cannot distinguish "reliably right" from "got lucky once" — 3-5 is a reasonable default.
              </p>
            </div>
            <div className="admin-aim-field">
              <label>Temperature (optional) <InfoTooltip title="Temperature" description={FIELD_INFO.temperature} /></label>
              <input type="number" min={0} max={1} step={0.1} value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="blank = provider default" />
            </div>
            <div className="admin-aim-field">
              <label>Judge model (DeepEval) <InfoTooltip title="Judge model" description={FIELD_INFO.judgeModel} /></label>
              <select value={judgeModel} onChange={(e) => setJudgeModel(e.target.value)}>
                <option value="">Default (Haiku 4.5)</option>
                {EVAL_MODEL_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div className="admin-aim-field">
              <label>Baseline run (optional — Δ comparison) <InfoTooltip title="Baseline run" description={FIELD_INFO.baselineRun} /></label>
              <select value={baselineRunId} onChange={(e) => setBaselineRunId(e.target.value)}>
                <option value="">— none —</option>
                {priorRuns.map((r) => (
                  <option key={r.id} value={r.id}>#{r.id} · {runEngine(r)} · {r.modelId} · {fmtDate(r.createdAt)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="admin-aim-field">
            <label>Mechanisms</label>
            <label style={{ display: 'block', fontSize: 13 }}>
              <input type="checkbox" checked={hasNative || toolsEnabled} disabled={hasNative} onChange={(e) => setToolsEnabled(e.target.checked)} /> Tools enabled
              {hasNative ? ' (always on for Ion Next engines)' : ''}
              <InfoTooltip title="Tools enabled" description={FIELD_INFO.mechanisms.tools_enabled} />
            </label>
            <label style={{ display: 'block', fontSize: 13 }}>
              <input type="checkbox" checked={webSearchEnabled} disabled={!(hasNative || toolsEnabled)} onChange={(e) => setWebSearchEnabled(e.target.checked)} /> Web search enabled
              <InfoTooltip title="Web search enabled" description={FIELD_INFO.mechanisms.web_search_enabled} />
            </label>
            <label style={{ display: 'block', fontSize: 13 }}>
              <input type="checkbox" checked={knowledgeEnabled} disabled={!(hasNative || toolsEnabled)} onChange={(e) => setKnowledgeEnabled(e.target.checked)} /> Knowledge retrieval enabled
              <InfoTooltip title="Knowledge retrieval enabled" description={FIELD_INFO.mechanisms.knowledge_enabled} />
            </label>
          </div>
          <div className="admin-aim-field">
            <label>Graders (deterministic scorer always runs)</label>
            {GRADER_OPTIONS.map((g) => (
              <label key={g.id} style={{ display: 'block', fontSize: 13 }}>
                <input type="checkbox" checked={graders.includes(g.id)} onChange={() => toggleGrader(g.id)} />
                {' '}{g.label}
                <InfoTooltip title={g.label} description={FIELD_INFO.graders[g.id]} />
              </label>
            ))}
            {/* Advisory only — never blocks the run. A grader whose per-case
                input is empty produces no score at all, and nothing else in the
                UI says so. */}
            <p className="admin-aim-field-hint">
              Grader coverage in this selection: {graderCoverage.withReferenceAnswer}/
              {graderCoverage.total} cases have a reference answer (hallucination),{' '}
              {graderCoverage.withRubric}/{graderCoverage.total} have rubric text (GEval).
            </p>
            {graders.includes('hallucination') && graderCoverage.withReferenceAnswer === 0 && (
              <p className="admin-aim-field-hint admin-aim-field-hint--warn">
                No selected case has a reference answer, so the hallucination grader would grade
                nothing — it still costs a judge call per case. Fill in Reference answer on the
                cases first, or drop this grader.
              </p>
            )}
          </div>

          {estimate && (
            <div className="admin-aim-error-banner" style={{ background: 'var(--warning, #c98a0d)' }}>
              {estimate.message} (estimated ${estimate.estimatedCostUsd?.toFixed?.(2)}, {estimate.nCases} cases × {isClassicOnly ? `${models.length} model(s)` : `${runCount} engine × model run(s)`} × {estimate.repeats} repeat(s))
              {headroomLine && (
                <div style={{ marginTop: 4, fontSize: 12 }}>{headroomLine}</div>
              )}
              {/*
                Advisory, not a block. Hard blocks arrive as 429s (`blocked`
                below): the per-run cost ceiling, and an estimate that already
                exceeds the eval account's remaining MONTHLY or DAILY budget
                (daily = the global runaway brake standing in for the account's
                unlimited daily window). This flag is what's left — in the
                shipped configuration only the WEEKLY window, which has no brake
                behind it and is therefore unlimited in practice. It stays
                generic so a finite weekly eval budget behaves sanely the moment
                someone configures one. Such a run starts, then aborts between
                turns on the per-turn quota gate and lands as `partial`, so warn
                first.
              */}
              {estimate.exceedsHeadroom && (
                <div style={{ marginTop: 4, fontWeight: 600 }}>
                  This run would exceed the Ion eval account's remaining budget for that
                  window — it will start, then stop as a <code>partial</code> run once the
                  window is used up. Raise the eval account's limit first (the eval budget
                  card above the runs list, or its row in AI Management → Usages), or
                  re-scope the selection.
                </div>
              )}
            </div>
          )}
          {blocked && (
            <div className="admin-aim-error-banner">
              <div style={{ fontWeight: 600 }}>
                {blocked.kind === 'eval_budget'
                  ? 'Eval budget exhausted — nothing was started'
                  : blocked.kind === 'ion_next_daily_cap'
                    ? 'Ion Next daily cap reached — nothing was started'
                    : 'Run cost ceiling exceeded — nothing was started'}
              </div>
              {/* The server message names the remaining budget and reset date —
                  show it verbatim rather than a generic failure string. */}
              <div style={{ fontSize: 12, marginTop: 4 }}>{blocked.message}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {blocked.kind === 'eval_budget'
                  ? "Confirming can't override this — eval spend is billed to the Ion eval account, and its window has no room left."
                  : blocked.kind === 'ion_next_daily_cap'
                    ? "Confirming can't override Ion Next's daily cap, which all native traffic shares."
                    : "Confirming can't override the per-run cost ceiling."}
              </div>
            </div>
          )}
          {error && <div className="admin-aim-error">{error}</div>}
        </div>
        <footer className="admin-aim-drawer-footer">
          {estimate ? (
            <button type="button" className="admin-ai-btn admin-ai-btn--send" onClick={() => handleSubmit(true)} disabled={submitting}>
              {submitting ? 'Starting…' : `Confirm — spend ~$${estimate.estimatedCostUsd?.toFixed?.(2)}`}
            </button>
          ) : (
            <button type="button" className="admin-ai-btn admin-ai-btn--send" onClick={() => handleSubmit(false)} disabled={submitting}>
              {submitting ? 'Estimating…' : 'Run eval'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function CompareGroupView({ compareGroupId, onClose }) {
  const [group, setGroup] = useState(null);
  const [error, setError] = useState(null);
  const [openRunId, setOpenRunId] = useState(null);
  const [dlError, setDlError] = useState(null);

  const handleDownloadBundle = async () => {
    setDlError(null);
    try {
      const bundle = await exportIonEvalRunGroupBundle(compareGroupId);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadJsonBundle(bundle, `ion-eval-compare-${compareGroupId.slice(0, 8)}-${stamp}.json`);
    } catch (e) {
      setDlError(e?.message || 'download failed');
    }
  };

  const handleDownloadMarkdown = async () => {
    setDlError(null);
    try {
      const md = await exportIonEvalRunGroupMarkdown(compareGroupId);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadMarkdownReport(md, `ion-eval-compare-${compareGroupId.slice(0, 8)}-${stamp}.md`);
    } catch (e) {
      setDlError(e?.message || 'download failed');
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = () => {
      getIonEvalRunGroup(compareGroupId)
        .then((data) => {
          if (cancelled) return;
          setGroup(data);
          const stillGoing = (data.runs || []).some((r) => r.status === 'queued' || r.status === 'running');
          if (stillGoing) timer = setTimeout(load, 4000);
        })
        .catch((e) => {
          if (!cancelled) setError(e?.message || 'network');
        });
    };
    load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [compareGroupId]);

  const runs = group?.runs || [];

  return (
    <>
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div className="admin-aim-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">Model comparison</div>
            <div className="admin-aim-drawer-sub">
              Sequential execution — one engine × model runs at a time; the rest wait their turn.
            </div>
            {dlError && <div className="admin-aim-error">Bundle download failed: {dlError}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleDownloadBundle}
              disabled={runs.length === 0}
              title="Download all sibling runs of this comparison as one JSON bundle (each run's summary + per-case trajectories + judge scores)."
            >
              ⬇ Download bundle (JSON)
            </button>
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleDownloadMarkdown}
              disabled={runs.length === 0}
              title="Download this comparison as one self-contained Markdown report — side-by-side headline table plus every run's full per-case diagnosis. Built to paste into an AI assistant."
            >
              ⬇ Download report (Markdown)
            </button>
            <button type="button" className="admin-aim-icon-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
        </header>
        <div className="admin-aim-drawer-body">
          {error && <div className="admin-aim-error">{error}</div>}
          {!group && !error && <p>Loading…</p>}
          {group && (
            <div className="admin-aim-table-wrap">
              <table className="admin-aim-table">
                <thead>
                  <tr>
                    <th>Engine</th>
                    <th>Model</th>
                    <th>Status</th>
                    <th>pass@k <InfoTooltip title={metricLabel('pass_at_k_rate')} description={metricTooltip('pass_at_k_rate')} /></th>
                    <th>pass^k <InfoTooltip title={metricLabel('pass_hat_k_rate')} description={metricTooltip('pass_hat_k_rate')} /></th>
                    <th>Task completion <InfoTooltip title={metricLabel('task_completion_rate')} description={metricTooltip('task_completion_rate')} /></th>
                    <th>Cost</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id}>
                      <td>{runEngine(r)}</td>
                      <td>{r.modelId}</td>
                      <td>
                        {r.status === 'queued' ? (
                          <span className="admin-badge">waiting</span>
                        ) : r.status === 'running' ? (
                          <span className="admin-badge admin-badge--basic">
                            running {r.progress?.cases_done ?? 0}/{r.progress?.cases_total ?? '?'}
                          </span>
                        ) : (
                          <RunStatusBadge status={r.status} />
                        )}
                        {r.status === 'partial' && (
                          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                            budget ran out — {r.nCases ?? '?'} case(s) scored
                          </div>
                        )}
                      </td>
                      <td>{fmtPct(r.passAtKRate)}</td>
                      <td>{fmtPct(r.passHatKRate)}</td>
                      <td>{fmtPct(r.taskCompletionRate)}</td>
                      <td>{fmtCost(r.actualCostUsd ?? r.estimatedCostUsd)}</td>
                      <td>
                        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={() => setOpenRunId(r.id)}>
                          View cases
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
    {openRunId != null && (
      <RunDetail runId={openRunId} onClose={() => setOpenRunId(null)} />
    )}
    </>
  );
}

/**
 * Reset instant of a quota window. The backend sends a NAIVE ISO datetime
 * (UTC-derived, no `Z`), so `new Date()` reads it as local time — harmless for
 * a date-only rendering of a midnight boundary, and deliberately not converted,
 * since inventing a timezone shift would move the printed day.
 */
function fmtResetDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/** Usage bar, same classes/tones as AI Management's per-user bars. */
function BudgetBar({ pct }) {
  const safe = Math.min(100, Math.max(0, pct ?? 0));
  const tone = safe >= 90 ? 'critical' : safe >= 60 ? 'warn' : 'ok';
  return (
    <div className="admin-aim-bar" style={{ width: '100%', maxWidth: 420 }}>
      <div className={`admin-aim-bar-fill is-${tone}`} style={{ width: `${safe}%` }} />
    </div>
  );
}

/**
 * The eval budget at the top of the Runs view — what the eval suite may spend
 * before a run gets refused, and the one-click way to raise it for this month.
 *
 * WHOSE BUDGET: the synthetic Ion eval account's (`chat/eval/eval_user.py`),
 * which is the billing identity of every eval run. Never the viewing admin's.
 *
 * Two things this card exists to prevent, because both are easy to misread:
 *
 *  1. **A top-up is temporary.** It is an `ion_limit_topups` row scoped to the
 *     current cycle, so it silently evaporates at `monthlyResetsAt` — unlike
 *     raising the BASE monthly limit, which persists. The base and the top-up
 *     are therefore shown as separate numbers that add up to the effective
 *     ceiling, with the expiry date spelled out, rather than as one total that
 *     would look permanent.
 *  2. **The daily figure is a brake, not a tier limit.** The eval account's
 *     daily override is the unlimited sentinel, and `ensure_within_quota`
 *     substitutes the global runaway cap
 *     (`tier_limits_config.daily_usd_unlimited_cap`) for it. That cap is a real
 *     ceiling — it is what turns a big run into a `partial` one part-way
 *     through the day — so when `dailyIsBrake` is set the number is rendered as
 *     a ceiling and labelled as the brake. "Today: unlimited" would be a lie in
 *     the shipped configuration.
 */
function EvalBudgetCard() {
  const [budget, setBudget] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupSaving, setTopupSaving] = useState(false);
  const [topupMessage, setTopupMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setBudget(await getIonEvalBudget());
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleGrantTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setTopupMessage({ type: 'error', text: 'Enter an amount > 0.' });
      return;
    }
    setTopupSaving(true);
    setTopupMessage(null);
    try {
      // The 201 body carries the recomputed budget including this grant, so the
      // card repaints from one round-trip (no re-GET that could race).
      const result = await grantIonEvalBudgetTopup({ amountUsd: amount, note: topupNote || null });
      if (result?.budget) setBudget(result.budget);
      setTopupAmount('');
      setTopupNote('');
      const until = fmtResetDate(result?.budget?.monthlyResetsAt ?? budget?.monthlyResetsAt);
      setTopupMessage({
        type: 'success',
        text:
          `+$${amount.toFixed(2)} added to the eval account's monthly ceiling for this cycle` +
          (until ? ` — expires ${until}.` : '.'),
      });
    } catch (e) {
      setTopupMessage({ type: 'error', text: e?.message || 'network' });
    } finally {
      setTopupSaving(false);
    }
  };

  const resetsAt = fmtResetDate(budget?.monthlyResetsAt);
  const hasTopup = Number(budget?.activeTopupUsd || 0) > 0;

  return (
    <section className="admin-aim-tier-section" style={{ borderTop: 'none', paddingTop: 0 }}>
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title" style={{ fontSize: 15 }}>Ion eval budget</h2>
          <p className="admin-aim-sub">
            Eval runs are billed to the synthetic Ion eval account
            {budget?.evalUserEmail ? <> (<code>{budget.evalUserEmail}</code>)</> : null}, never to
            you. When this budget runs out, a run is refused before it starts — or aborts
            mid-flight and lands as <code>partial</code>.
          </p>
        </div>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={load}
          disabled={loading}
          title="Reload the eval budget."
        >
          ↻
        </button>
      </header>

      {error && <div className="admin-aim-error-banner">Eval budget unavailable: {error}</div>}
      {loading && !budget && <p className="admin-aim-field-hint">Loading budget…</p>}

      {budget && (
        <>
          <div className="admin-aim-field-grid">
            <div>
              <label className="admin-aim-bar-label" style={{ position: 'static', display: 'block' }}>
                This month
              </label>
              {budget.effectiveMonthlyUsd == null ? (
                <div style={{ fontSize: 13 }}>
                  ${budget.monthlyUsedUsd} used — no monthly ceiling resolved
                  <span className="admin-aim-field-hint" style={{ display: 'block' }}>
                    The eval account's monthly limit could not be resolved (or is unlimited).
                    Only the daily figure below and the per-run cost ceiling bound a run.
                  </span>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>${budget.monthlyUsedUsd}</strong> of ${budget.effectiveMonthlyUsd} used
                    {' '}({budget.monthlyUsedPct}%)
                  </div>
                  <BudgetBar pct={budget.monthlyUsedPct} />
                  <div className="admin-aim-bar-meta">
                    ${budget.monthlyRemainingUsd ?? '0.00'} remaining
                    {resetsAt ? ` · resets ${resetsAt}` : ''}
                  </div>
                </>
              )}
            </div>

            <div>
              <label className="admin-aim-bar-label" style={{ position: 'static', display: 'block' }}>
                Today
              </label>
              {budget.dailyLimitUsd == null ? (
                <div style={{ fontSize: 13 }}>
                  <strong>${budget.dailyUsedUsd}</strong> used — no daily cap
                  <span className="admin-aim-field-hint" style={{ display: 'block' }}>
                    The global runaway brake is switched off (cap 0), so nothing bounds a single
                    day apart from the monthly budget above.
                  </span>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13 }}>
                    <strong>${budget.dailyUsedUsd}</strong> of ${budget.dailyLimitUsd}
                    {budget.dailyIsBrake ? ' brake' : ''} used
                  </div>
                  <div className="admin-aim-bar-meta">
                    ${budget.dailyRemainingUsd ?? '0.00'} remaining today · resets 00:00 UTC
                  </div>
                  {/*
                    Say where the number comes from. The eval account is configured
                    "daily: unlimited", so an unexplained "$45.90 left today" reads as
                    a bug — and calling it unlimited would hide the ceiling that
                    actually stops runs.
                  */}
                  <p className="admin-aim-field-hint">
                    {budget.dailyIsBrake ? (
                      <>
                        This is the <strong>global runaway brake</strong>, not a tier limit: the
                        eval account's daily window is set to unlimited, so enforcement
                        substitutes the platform-wide daily cap. It is still a hard ceiling — a
                        run that overruns it aborts and lands as <code>partial</code>. Raise it in
                        AI Management → tier limits (daily unlimited cap), not with a top-up.
                      </>
                    ) : (
                      <>Daily ceiling from the eval account's tier limit / per-user override.</>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>

          {/*
            Base vs top-up, deliberately as two numbers. One is permanent, the
            other disappears at the reset date; a single "$1250" would hide that.
          */}
          <p className="admin-aim-field-hint" style={{ marginTop: 10 }}>
            Monthly ceiling ={' '}
            <strong>${budget.monthlyLimitUsd ?? '—'}</strong> base limit (permanent — changed via{' '}
            <code>ION_EVAL_USER_MONTHLY_LIMIT_USD</code> or the eval account's per-user monthly
            override in AI Management){hasTopup ? (
              <>
                {' '}+ <strong>${budget.activeTopupUsd}</strong> top-up granted for this cycle
                (<strong>temporary</strong> — it expires{resetsAt ? ` ${resetsAt}` : ' at the reset'}
                {' '}and the ceiling drops back to ${budget.monthlyLimitUsd ?? '—'})
              </>
            ) : (
              <> + no active top-up this cycle</>
            )}.
          </p>

          <div className="admin-aim-topup-section">
            <label>Increase for this month</label>
            <p className="admin-aim-field-hint">
              A one-off raise of the eval account's monthly ceiling for the{' '}
              <strong>current cycle only</strong>. It applies to the next eval turn and expires by
              itself{resetsAt ? ` on ${resetsAt}` : ' at the cycle boundary'} — it is not a base-limit
              change, and it does not affect the daily brake.
            </p>
            <div className="admin-aim-field-grid">
              <div className="admin-aim-field">
                <label>Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={topupAmount}
                  placeholder="e.g. 250.00"
                  onChange={(e) => setTopupAmount(e.target.value)}
                />
              </div>
              <div className="admin-aim-field">
                <label>Note (audit-log)</label>
                <input
                  type="text"
                  value={topupNote}
                  maxLength={255}
                  placeholder="reason, e.g. extra comparison run"
                  onChange={(e) => setTopupNote(e.target.value)}
                />
              </div>
            </div>
            {topupMessage && (
              <div className={topupMessage.type === 'error' ? 'admin-aim-error' : 'admin-aim-success'}>
                {topupMessage.text}
              </div>
            )}
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleGrantTopup}
              disabled={topupSaving}
            >
              {topupSaving ? 'Granting…' : 'Grant top-up for this month'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function RunsView({ preset = null }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openRunId, setOpenRunId] = useState(null);
  const [openCompareGroupId, setOpenCompareGroupId] = useState(null);
  const [configuring, setConfiguring] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getIonEvalRuns({ limit: 100 });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmitted = (result) => {
    setConfiguring(false);
    load();
    if (result.compareGroupId) {
      setOpenCompareGroupId(result.compareGroupId);
    } else if (result.runIds?.length) {
      setOpenRunId(result.runIds[0]);
    }
  };

  return (
    <div className="admin-aim-subtab-body">
      {/* The eval overview: what the suite may still spend before a run is
          refused or cut short. Above the runs list because it is the thing to
          check BEFORE configuring a run, not after one came back `partial`. */}
      <EvalBudgetCard />

      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">{preset?.title ? `${preset.title} runs` : 'Eval runs'}</h2>
          {preset ? (
            <p className="admin-aim-sub">
              The eval runs list with the configurator preset to {preset.engines.join(' + ')} on{' '}
              {preset.models.join(', ')} and the <code>{preset.suite}</code> suite. Baseline,
              Sonnet 5.5 and other suites stay selectable; the cost confirm is the same.
            </p>
          ) : (
            <p className="admin-aim-sub">
              Trigger a real, cost-gated Bedrock run against curated cases, or land
              one via <code>scripts/ion/import_eval_run.py</code>.
            </p>
          )}
        </div>
        <div>
          <button type="button" className="admin-ai-btn admin-ai-btn--send" onClick={() => setConfiguring(true)}>
            Configure run
          </button>
          {' '}
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={load}>↻</button>
        </div>
      </header>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Engine</th>
              <th>Model</th>
              <th>Case set</th>
              <th>Cases</th>
              <th>pass@k <InfoTooltip title={metricLabel('pass_at_k_rate')} description={metricTooltip('pass_at_k_rate')} /></th>
              <th>pass^k <InfoTooltip title={metricLabel('pass_hat_k_rate')} description={metricTooltip('pass_hat_k_rate')} /></th>
              <th>Cost</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={10} className="admin-aim-empty">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={10} className="admin-aim-empty">No eval runs imported yet.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} onClick={() => setOpenRunId(r.id)} style={{ cursor: 'pointer' }}>
                <td className="admin-aim-rel">{fmtDate(r.createdAt)}</td>
                <td>{runEngine(r)}</td>
                <td>{r.modelId || '—'}</td>
                <td>{r.caseSet || '—'}</td>
                <td>{r.nCases ?? '—'}</td>
                <td>{fmtPct(r.passAtKRate)}</td>
                <td>{fmtPct(r.passHatKRate)}</td>
                <td>{fmtCost(r.actualCostUsd ?? r.estimatedCostUsd)}</td>
                <td><RunStatusBadge status={r.status} /></td>
                <td>▸</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-aim-meta">{total} run(s) total</div>

      {openRunId != null && (
        <RunDetail runId={openRunId} onClose={() => setOpenRunId(null)} />
      )}
      {openCompareGroupId != null && (
        <CompareGroupView compareGroupId={openCompareGroupId} onClose={() => setOpenCompareGroupId(null)} />
      )}
      {configuring && (
        <RunConfiguratorDrawer preset={preset} onClose={() => setConfiguring(false)} onSubmitted={handleSubmitted} />
      )}
    </div>
  );
}

function CandidatesView() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getIonEvalCandidates({ limit: 100 });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Eval candidates</h2>
          <p className="admin-aim-sub">
            Positive Ion feedback flagged as an eval-suite template. To turn one
            into a case, add it to <code>backend/app/api/v1/chat/eval/cases/cases.json</code>
            in a merge request — paraphrasing the prompt, which is why this is a
            review step and not a button.
          </p>
        </div>
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={load}>↻</button>
      </header>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Conversation</th>
              <th>Observed behaviour</th>
              <th>Reference tools</th>
              <th>Model</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={5} className="admin-aim-empty">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="admin-aim-empty">No positive eval-candidate feedback yet.</td></tr>
            ) : items.map((c) => (
              <tr key={c.feedbackId}>
                <td className="admin-aim-rel">{fmtDate(c.createdAt)}</td>
                <td>{c.conversationTitle || c.conversationId}</td>
                <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{c.observedBehaviour}</td>
                <td>{(c.referenceTools || []).join(', ') || '—'}</td>
                <td>{c.modelId || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-aim-meta">{total} candidate(s) total</div>
    </div>
  );
}

/**
 * `preset` (optional, e.g. ION_NEXT_EVAL_PRESET): {title, engines, models,
 * suite} the run configurator opens with. Without it this is the classic eval
 * dashboard, unchanged.
 */
export function AdminIonEvalSubtab({ preset = null } = {}) {
  const [view, setView] = useState('runs');

  const views = useMemo(
    () => [
      { id: 'runs', label: 'Runs' },
      { id: 'candidates', label: 'Candidates' },
      { id: 'cases', label: 'Cases' },
    ],
    []
  );

  return (
    <div className="admin-analytics-tab">
      <div className="analytics-header">
        <div className="analytics-nav" role="tablist" aria-label="Eval dashboard sections">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={`analytics-nav-btn ${view === v.id ? 'active' : ''}`}
              onClick={() => setView(v.id)}
            >
              <span>{v.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="analytics-content">
        {view === 'runs' && <RunsView preset={preset} />}
        {view === 'candidates' && <CandidatesView />}
        {view === 'cases' && <AdminIonEvalCasesSubtab />}
      </div>
    </div>
  );
}
